import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { portalApi } from '@/services/portal-api';

export type RadarTipo = 'mensagem' | 'convocacao' | 'mudanca_status' | 'documento' | 'outro';

export type RadarSeveridade = 'informativa' | 'atencao' | 'acao_necessaria' | 'critica';

export type RadarEstado =
  | 'nao_configurado'
  | 'em_descoberta'
  | 'validacao_tecnica'
  | 'validacao_juridica'
  | 'piloto'
  | 'ativo'
  | 'degradado'
  | 'pausado'
  | 'revogado';

export interface RadarResumoItem {
  pipelineId: number;
  naoLidas: number;
  acionaveis: number;
  ultimaEm: string | null;
}

export interface RadarResumo {
  total: number;
  acionaveis: number;
  porPipeline: RadarResumoItem[];
}

export interface RadarComunicacao {
  id: number;
  tipo: RadarTipo;
  autor: string | null;
  conteudo: string;
  publicado_em: string | null;
  observado_em: string;
  id_externo: string | null;
  /** Chave de storage (`storage://radar/...`). NUNCA renderizar o valor cru. */
  evidencia_referencia: string | null;
  lido: boolean;
  alerta_id: number | null;
  severidade: RadarSeveridade | null;
  acao_recomendada: string | null;
  alerta_status: string | null;
  motivo: string | null;
}

export interface RadarSnapshotItem {
  codigo: string | number | null;
  descricao: string | null;
  unidade?: string | null;
  quantidade?: number | string | null;
  valorReferencia?: number | string | null;
  melhorLance?: number | string | null;
  situacao?: string | null;
  participacao?: string | null;
}

export interface RadarSnapshotOperador {
  papel: string | null;
  nome: string | null;
}

export interface RadarSnapshot {
  atualizadoEm?: string | null;
  status?: string | null;
  statusCodigo?: string | number | null;
  operacao?: string | null;
  tipoLicitacao?: string | null;
  tipoJulgamento?: string | null;
  operador?: RadarSnapshotOperador | null;
  dataHoraAbertura?: string | null;
  isLote?: boolean | null;
  itens?: RadarSnapshotItem[];
  lotes?: RadarSnapshotItem[];
}

export interface RadarSessao {
  id: number;
  portal: string;
  portalNome: string | null;
  sessaoExternaId: string;
  urlOrigem: string | null;
  estado: RadarEstado | string;
  estadoIntegracao: RadarEstado | string;
  ultimaVerificacaoEm: string | null;
  erroSanitizado: string | null;
  snapshotAtualizadoEm: string | null;
  snapshot: RadarSnapshot;
  /** ENR.3 — fornecedores distintos observados na disputa (parcial/anonimizado; null = sem observação). */
  participantesContagem?: number | null;
  participantesObservadoEm?: string | null;
}

export interface RadarPaginacao {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface RadarTimeline {
  /** `true` quando a licitação não tem sessão monitorada (404 do backend). */
  semMonitoramento: boolean;
  sessao: RadarSessao | null;
  itens: RadarComunicacao[];
  pagination: RadarPaginacao;
}

export interface RadarPalavraChave {
  id: number;
  termo: string;
  ativo: boolean;
  created_at: string;
}

export interface PreferenciaNotificacao {
  canal: 'push_web' | 'email' | 'whatsapp';
  severidades: RadarSeveridade[];
  consentido_em: string | null;
  silencio_inicio: string | null;
  silencio_fim: string | null;
  destino: string | null;
}

export interface RadarEscopo {
  /** Presente apenas na visão da equipe (`/empresas/:id/...`), somente leitura. */
  empresaId?: number;
}

export const PALAVRAS_CHAVE_LIMITE = 30;
export const PALAVRA_CHAVE_MIN = 3;
export const PALAVRA_CHAVE_MAX = 120;

interface RespostaApi<T> {
  success: boolean;
  data: T;
}

type ErroDesconhecido = {
  status?: number;
  code?: string;
  message?: string;
  error?: { code?: string; message?: string };
  response?: { status?: number; data?: { error?: { code?: string; message?: string } } };
};

/**
 * O cliente HTTP devolve o corpo do erro como veio do backend
 * (`{ success: false, error: { code, message } }`), mas o fallback de rede
 * devolve `{ code, message, status }`. Lemos as duas formas.
 */
export function codigoErroRadar(error: unknown): string | null {
  const valor = error as ErroDesconhecido | undefined;
  return valor?.error?.code || valor?.response?.data?.error?.code || valor?.code || null;
}

export function statusErroRadar(error: unknown): number | null {
  const valor = error as ErroDesconhecido | undefined;
  return valor?.status ?? valor?.response?.status ?? null;
}

export function semMonitoramentoConectado(error: unknown): boolean {
  return codigoErroRadar(error) === 'NOT_FOUND' || statusErroRadar(error) === 404;
}

const MENSAGENS: Record<string, string> = {
  INVALID_KEYWORD: `O termo precisa ter de ${PALAVRA_CHAVE_MIN} a ${PALAVRA_CHAVE_MAX} caracteres.`,
  KEYWORD_LIMIT_REACHED: `Você já monitora ${PALAVRAS_CHAVE_LIMITE} palavras-chave, que é o limite. Remova uma para adicionar outra.`,
  INVALID_NOTIFICATION_DESTINATION:
    'Informe um número de WhatsApp válido com DDD. Sem um número válido nenhum alerta é enviado.',
  INVALID_NOTIFICATION_PREFERENCE: 'Revise as severidades e o horário de silêncio: a combinação enviada é inválida.',
  INVALID_COMMUNICATION_TYPE: 'Esse tipo de comunicação não existe. Escolha outro filtro.',
};

/** Mensagem em pt-BR para os códigos de erro do Radar, com fallback honesto. */
export function mensagemErroRadar(error: unknown, fallback = 'Não foi possível concluir a operação agora.'): string {
  const codigo = codigoErroRadar(error);
  if (codigo && MENSAGENS[codigo]) return MENSAGENS[codigo];
  const valor = error as ErroDesconhecido | undefined;
  return valor?.error?.message || valor?.message || fallback;
}

const escopoChave = (empresaId?: number) => (empresaId ? `empresa:${empresaId}` : 'portal');

const cliente = (empresaId?: number) => (empresaId ? api : portalApi);

const raizResumo = (empresaId?: number) => (empresaId ? `/empresas/${empresaId}/radar` : '/portal/radar');

export const radarResumoQueryKey = (empresaId?: number) => ['radar', 'resumo', escopoChave(empresaId)] as const;

export const radarTimelineQueryKey = (pipelineId: number, empresaId?: number) =>
  ['radar', 'timeline', escopoChave(empresaId), pipelineId] as const;

export const palavrasChaveQueryKey = () => ['radar', 'palavras-chave'] as const;

export const preferenciasNotificacaoQueryKey = () => ['radar', 'notificacoes-preferencias'] as const;

const paginacaoVazia = (page: number, limit: number): RadarPaginacao => ({ page, limit, total: 0, totalPages: 1 });

export interface UseRadarResumoOptions extends RadarEscopo {
  /** Polling de retaguarda enquanto o SSE está fora. */
  intervaloMs?: number;
  enabled?: boolean;
}

/** Contador de não lidas por licitação — alimenta o badge do botão de chat. */
export function useRadarResumo({ empresaId, intervaloMs = 60_000, enabled = true }: UseRadarResumoOptions = {}) {
  return useQuery({
    queryKey: radarResumoQueryKey(empresaId),
    enabled,
    queryFn: async (): Promise<RadarResumo> => {
      const resposta = await cliente(empresaId).get<RespostaApi<RadarResumo>>(`${raizResumo(empresaId)}/resumo`);
      const dados = resposta?.data;
      return {
        total: dados?.total || 0,
        acionaveis: dados?.acionaveis || 0,
        porPipeline: Array.isArray(dados?.porPipeline) ? dados.porPipeline : [],
      };
    },
    refetchInterval: intervaloMs,
  });
}

export interface UseRadarTimelineParams extends RadarEscopo {
  pipelineId: number;
  tipo?: RadarTipo | '';
  busca?: string;
  page?: number;
  limit?: number;
  /** Polling de retaguarda: é o fallback oficial quando o SSE cai. */
  intervaloMs?: number;
  enabled?: boolean;
}

/**
 * Timeline do andamento. O 404 do backend (licitação sem sessão monitorada)
 * NÃO é erro: vira `semMonitoramento`, para a tela dizer isso com todas as
 * letras em vez de mostrar uma timeline vazia que parece bug.
 */
export function useRadarTimeline({
  pipelineId,
  empresaId,
  tipo,
  busca,
  page = 1,
  limit = 50,
  intervaloMs = 5_000,
  enabled = true,
}: UseRadarTimelineParams) {
  const buscaNormalizada = busca?.trim() || '';
  const filtros = { tipo: tipo || '', busca: buscaNormalizada, page, limit };

  return useQuery({
    queryKey: [...radarTimelineQueryKey(pipelineId, empresaId), filtros],
    enabled: enabled && Number.isFinite(pipelineId) && pipelineId > 0,
    queryFn: async (): Promise<RadarTimeline> => {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (tipo) params.set('tipo', tipo);
      if (buscaNormalizada) params.set('busca', buscaNormalizada);

      const raiz = empresaId ? `/empresas/${empresaId}/radar` : '/portal/radar';
      try {
        const resposta = await cliente(empresaId).get<RespostaApi<RadarTimeline>>(
          `${raiz}/licitacoes/${pipelineId}/comunicacoes?${params.toString()}`
        );
        const dados = resposta?.data;
        if (!dados?.sessao) {
          return { semMonitoramento: true, sessao: null, itens: [], pagination: paginacaoVazia(page, limit) };
        }
        return {
          semMonitoramento: false,
          sessao: { ...dados.sessao, snapshot: dados.sessao.snapshot || {} },
          itens: Array.isArray(dados.itens) ? dados.itens : [],
          pagination: dados.pagination || paginacaoVazia(page, limit),
        };
      } catch (error) {
        if (semMonitoramentoConectado(error)) {
          return { semMonitoramento: true, sessao: null, itens: [], pagination: paginacaoVazia(page, limit) };
        }
        throw error;
      }
    },
    refetchInterval: (query) => (query.state.data?.semMonitoramento ? false : intervaloMs),
  });
}

/** Marca comunicações como lidas. Sem `comunicacaoIds`, marca tudo da licitação. */
export function useMarcarComunicacoesLidas(pipelineId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (comunicacaoIds?: number[]) =>
      portalApi.patch<RespostaApi<{ marcadas: number }>>(
        `/portal/radar/licitacoes/${pipelineId}/comunicacoes/lidas`,
        comunicacaoIds?.length ? { comunicacaoIds } : undefined
      ),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: radarTimelineQueryKey(pipelineId) }),
        queryClient.invalidateQueries({ queryKey: ['radar', 'resumo'] }),
      ]),
  });
}

export function usePalavrasChave() {
  return useQuery({
    queryKey: palavrasChaveQueryKey(),
    queryFn: async (): Promise<RadarPalavraChave[]> => {
      const resposta = await portalApi.get<RespostaApi<RadarPalavraChave[]>>('/portal/radar/palavras-chave');
      return Array.isArray(resposta?.data) ? resposta.data : [];
    },
  });
}

export function useCriarPalavraChave() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (termo: string) => portalApi.post('/portal/radar/palavras-chave', { termo: termo.trim() }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: palavrasChaveQueryKey() }),
  });
}

export function useRemoverPalavraChave() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => portalApi.delete(`/portal/radar/palavras-chave/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: palavrasChaveQueryKey() }),
  });
}

export function usePreferenciasNotificacao() {
  return useQuery({
    queryKey: preferenciasNotificacaoQueryKey(),
    queryFn: async (): Promise<PreferenciaNotificacao[]> => {
      const resposta = await portalApi.get<RespostaApi<PreferenciaNotificacao[]> | PreferenciaNotificacao[]>(
        '/portal/notificacoes/preferencias'
      );
      const bruto = Array.isArray(resposta) ? resposta : (resposta as RespostaApi<PreferenciaNotificacao[]>)?.data;
      return Array.isArray(bruto) ? bruto : [];
    },
  });
}

export interface SalvarPreferenciaWhatsAppInput {
  consentido: boolean;
  destino: string | null;
  severidades: RadarSeveridade[];
  silencioInicio?: string | null;
  silencioFim?: string | null;
}

export function useSalvarPreferenciaWhatsApp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SalvarPreferenciaWhatsAppInput) =>
      portalApi.put('/portal/notificacoes/preferencias/whatsapp', {
        severidades: payload.severidades,
        consentido: payload.consentido,
        destino: payload.destino || undefined,
        silencioInicio: payload.silencioInicio || undefined,
        silencioFim: payload.silencioFim || undefined,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: preferenciasNotificacaoQueryKey() }),
  });
}
